"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/Button";
import { GridBackground } from "@/components/GridBackground";
import { Spotlight, useSpotlight } from "@/components/Spotlight";
import clsx from "@/helpers/clsx";
import { useMaxBreakpoint } from "@/helpers/useBreakpoint";
import imageHeroDashboard from "@/public/_static/images/hero-dashboard-hires.png";
import imageHeroPortfolio from "@/public/_static/images/hero-portfolio-hires.png";
import noiseImage from "@/public/_static/images/noise2.webp";
import { ArrowRightIcon } from "@heroicons/react/20/solid";
import * as d3 from "d3";
import { motion, useMotionTemplate, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FlipWords } from "./FlipWords";
import { LastUpdate, LastUpdateProp } from "./LastUpdate";
import { Lens } from "./Lens";
import { LogoWall } from "./LogoWall";
const TILT_MAX = 33;
const START_OPACITY = 0.6;
const MotionLens = motion.create(Lens as any);
const texts = ["Automate your FundOps™. Unlock portfolio insights. Scale with software – not spreadsheets and backoffice headcount.", "Run your fund like a startup. Fast, data-driven, and automated – everything legacy fund management platforms aren't."];
export function Hero({
  logos,
  lastUpdate
}: {
  logos: any;
  lastUpdate?: LastUpdateProp;
}) {
  ReactDOM.preload(noiseImage.src, {
    as: "image"
  });
  const sectionRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const [isAnimated, setIsAnimated] = useState(false);
  const [isSecondImageVisible, setIsSecondImageVisible] = useState(false);
  const {
    containerProps,
    ...spotlightProps
  } = useSpotlight({
    size: 650,
    hideWhenInactive: true,
    key: String(isSecondImageVisible)
  });
  const {
    scrollY,
    scrollYProgress
  } = useScroll({
    target: sectionRef
  });
  const isSm = useMaxBreakpoint("sm");
  const maxScroll = 200;
  const rotateX = useTransform(scrollY,
  // Map x from these values:
  [0, maxScroll],
  // Into these values:
  [TILT_MAX, 0]);
  const scale = useTransform(scrollY,
  // Map x from these values:
  [0, maxScroll],
  // Into these values:
  [0.85, 1]);
  const opacity = useTransform(scrollY,
  // Map x from these values:
  [0, maxScroll * 0.5],
  // Into these values:
  [START_OPACITY, 1]);
  const titleBlur = useTransform(scrollY, [maxScroll - 100, maxScroll + 100], [0, 20]);
  const titleBlurValue = useMotionTemplate`blur(${titleBlur}px)`;
  const titleOpacity = useTransform(scrollY, [maxScroll - 100, maxScroll + 50, maxScroll + 300], [1, 0.3, 0]);
  const stickiness = useTransform(scrollY, [0, maxScroll + 300, maxScroll + 300], ["sticky", "sticky", "static"]);
  const height = useTransform(scrollY, [0, maxScroll], [20, 100]);
  const buttonOpacity = useTransform(scrollY, [30, 70], [1, 0]);
  useMotionValueEvent(scrollYProgress, "change", progress => {
    if (!isAnimated && progress > 0.01) {
      setIsAnimated(true);
    }
    let switchToSecondScreenWhenPctScrolled = 0.75;
    if (window.innerHeight < 500 && window.matchMedia("(orientation: landscape)").matches) {
      switchToSecondScreenWhenPctScrolled = 0.5;
    }
    const isSecondImageVisibleNow = progress > switchToSecondScreenWhenPctScrolled;
    if (isSecondImageVisible !== isSecondImageVisibleNow) {
      setIsSecondImageVisible(isSecondImageVisibleNow);
    }
  });
  useEffect(() => {
    //history.scrollRestoration = "manual";
    const isAnimated_0 = window.scrollY > 0;
    setIsAnimated(isAnimated_0);
  }, []);
  useLayoutEffect(() => {
    if (!heroRef.current) return;
    const heroEl = heroRef.current;
    const setHeroTopValue = () => {
      const screen = window.screen.height;
      const heroHeight = heroEl.getBoundingClientRect().height;
      const heroTop = (screen - heroHeight) / 2 - 72;
      heroEl.style.top = `${Math.max(92, heroTop)}px`;
    };
    setHeroTopValue();
    window.addEventListener("resize", setHeroTopValue, {
      passive: true
    });
    return () => {
      window.removeEventListener("resize", setHeroTopValue);
    };
  }, []);

  /*   const transform = useMotionTemplate`rotateX(${rotateX}deg) scale(${scale}) translateZ(0)`; */

  const [descriptionText, setDescriptionText] = useState("Automate your FundOps™. Unlock portfolio insights. Scale with software – not spreadsheets and backoffice headcount.");
  useEffect(() => {
    setDescriptionText(texts[Math.floor(Math.random() * texts.length)]);
  }, []);
  return <section id="hero" className="max-w-screen relative -mt-header" ref={sectionRef}>
      {/* <div className="pointer-events-none absolute inset-0 z-[99999] opacity-20">
        <LightRays
          raysOrigin="top-center"
          raysColor="#ffffff"
          raysSpeed={1.5}
          lightSpread={0.8}
          rayLength={1.2}
          followMouse={true}
          mouseInfluence={0.1}
          noiseAmount={0.1}
          distortion={0.05}
          className="custom-rays"
        />
       </div> */}
      <div className="relative flex min-h-[200vh] flex-col pb-4 text-center sm-landscape:min-h-[300vh]">
        <motion.div className={clsx("sticky top-0 z-[0] flex min-h-[max(450px,50vh)] flex-col justify-start pt-header [backface-visibility:hidden] sm-landscape:min-h-[300px] md:min-h-[max(600px,50vh)] md:justify-center"
      //"lg:pt-[max(200px,24vh)] pb-14 pt-32 sm:pt-40"
      )} initial={{
        position: "sticky"
      }} whileInView={{
        position: "sticky",
        z: 0.0001
      }} style={{
        filter: titleBlurValue,
        opacity: titleOpacity,
        z: 0.0001,
        position: stickiness
      }} transition={{
        type: "keyframes"
      }}>
          <div className="relative z-10 pt-[21px] lg:pt-16">
            {lastUpdate && <LastUpdate update={lastUpdate} className="z-[-1] mx-auto mb-5 transition-none md:absolute md:inset-x-auto md:-top-6 md:-translate-x-1/2 lg:top-0 lg:mt-1.5" />}
            <div className="sr-only hidden md:flex">
              The platform for a modern venture fund manager
            </div>
            <motion.h1 className={clsx("mx-auto flex max-w-[95vw] animate-fadeInUpBig flex-col items-center pb-0.5 font-fundra text-[30px] font-bold leading-[1.35] tracking-tight text-white animate-duration-1000 animate-delay-300 animate-distance-12 sm-landscape:text-5xl sm:text-5xl md:text-[58px] md:leading-[1.20] lg:max-w-5xl", "mt-[2vh] lg:mt-2.5"
          /* "bg-gradient-to-br from-white from-30% to-white/40 bg-clip-text text-transparent", */)} initial={{
            opacity: 0,
            filter: "blur(5px)",
            z: 0.0001
          }} animate={{
            opacity: 1,
            filter: "blur(0px)",
            z: 0.0001
          }} transition={{
            opacity: {
              delay: 0,
              duration: 0
            },
            delay: 0.5,
            type: "keyframes"
          }} style={{
            fontSize: "clamp(28px, 14.4px + 4.2614vw, 58px)"
          }}>
              <div className="hidden md:block">
                <FlipWords words={["modern", "data-driven", "leading"]}>
                  The platform for a{" "}
                </FlipWords>
                <div className="mx-auto inline-flex animate-fadeInUpBig text-white animate-delay-500 animate-distance-12">
                  venture fund manager
                </div>
              </div>
              <div className="block md:hidden">
                The platform for a<div className="block" />
                modern fund manager
              </div>

              {/* Manage your venture fund like the best do */}
              {/* A venture fund management from the future */}
              {/* The future of your venture fund management */}
            </motion.h1>
            <p className="mx-auto mt-3.5 max-w-[95vw] animate-fadeInUpBig text-[16px] !leading-relaxed tracking-tight text-gray-400 animate-delay-1000 animate-distance-2 [text-wrap:balance] sm-landscape:text-lg md:mt-6 md:max-w-2xl md:px-0 md:text-xl">
              {descriptionText}
            </p>
            <div className="absolute inset-x-0 mt-9 animate-fadeInUpBig animate-duration-1000 animate-delay-[1.2s] animate-distance-4 sm-landscape:hidden sm:mt-[36px] md:animate-distance-8 lg:mt-[max(100px,9vh)]"
          /* initial={{
            top: 0,
          }}
          whileInView={{
            top: "auto",
            transition: {
              duration: 0,
            },
          }} */>
              <Button variant="solid" color="primary" className="text-shadow-md text-shadow pointer-events-auto relative overflow-hidden rounded-lg px-5 py-3 shadow-circle2 shadow-primary/20 ring-[1px] ring-black/50 ring-offset-black transition-all duration-300 ease-in-out sm:px-6" data-cal-link="fundra/demo" data-cal-config={`{"subscribe":true}`}>
                Book a demo & start a free trial
                <ArrowRightIcon className="-mr-2.5 ml-2 size-4 sm:-mr-3" />
              </Button>
            </div>
          </div>
          <motion.div className="absolute inset-x-0 -bottom-[60px] inline-flex overflow-hidden md:-bottom-[135px]" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1,
          delay: 1.2
        }}>
            <div className="mx-auto inline-flex min-w-0">
              <LogoWall />
            </div>
          </motion.div>

          <Background logos={logos} />
        </motion.div>

        <div className="hero pointer-events-none sticky top-1/4 z-[1] mb-12 mt-[3vh] w-full max-w-7xl select-none self-center px-2 pb-12 [backface-visibility:hidden] [perspective:1280px] md:mx-0 md:mt-0 xl:-mx-8" ref={heroRef}>
          <motion.div
        //className="fade-hero [--mask-size:50%]"
        style={{
          ...(isAnimated && {
            rotateX,
            scale,
            translateY: 0,
            opacity
          })
        }} initial={{
          opacity: 0,
          y: 150,
          z: 0.001,
          rotateX: 0,
          scale: 1
        }} animate={{
          y: 0,
          z: 0.001,
          rotateX: TILT_MAX,
          scale: 0.85,
          opacity: START_OPACITY
        }} transition={{
          type: "keyframes",
          stiffness: 40,
          duration: 1,
          delay: 0.5
        }} {...containerProps} onAnimationComplete={() => {
          setIsAnimated(true);
        }}>
            <div className="group relative h-full shadow-circle2 shadow-black/20 [--radius:4px] md:[--radius:9px]">
              {/* <Spotlight
                {...spotlightProps}
                className="rounded-[var(--radius)] mix-blend-multiply"
                color="rgba(2055, 255, 255, 0.2)"
                size={400}
               /> */}

              <Spotlight {...spotlightProps} color="rgba(0, 0, 0, 0.5)" size={isSm ? 90 : 145} className="flex animate-fadeIn" />

              {/* <div className="absolute inset-0 overflow-hidden rounded-[var(--radius)]">
                <BorderFollower
                  className="pointer-events-auto relative z-50"
                  rx="9px"
                  ry="9px"
                  duration={6000}
                >
                  <div className="aspect-square w-[100px] bg-[radial-gradient(#10b981,transparent_65%)] opacity-50 group-hover:w-[500px] md:w-[420px] md:opacity-60" />
                </BorderFollower>
               </div> */}

              <div className="absolute inset-0 overflow-hidden">
                <div className="edgy-2 text-white/10">
                  <div className="edgy-border"></div>
                </div>

                <div className="edgy-2 rotate-180 text-white/10">
                  <div className="edgy-border"></div>
                </div>
              </div>

              <div className="no-drag pointer-events-auto relative h-full flex-1">
                {/* transform-gpu md:contrast-[105%] */}
                <Lens className={clsx(isSecondImageVisible && "pointer-events-none z-0")}>
                  <Image src={imageHeroDashboard} className={clsx("h-full rounded-[4px] border border-white/[5%] sm:border-white/10 md:rounded-[8px]")} width={3024} height={1752} alt="preview of the app's dashboard page" priority quality={95} />
                </Lens>
                <MotionLens className="absolute inset-0" variants={{
                hidden: {
                  opacity: 0,
                  pointerEvents: "none"
                },
                visible: {
                  opacity: 1,
                  pointerEvents: "auto"
                }
              }} initial={{
                opacity: 0
              }} transition={{
                ease: "easeIn"
              }} animate={isSecondImageVisible ? "visible" : "hidden"}>
                  <Image src={imageHeroPortfolio} className={clsx("rounded-[4px] border border-transparent md:rounded-[8px]")}
                /* width={3024}
                height={1752} */ width={3024} height={1752} alt="preview of the app's portfolio page" quality={85} />
                </MotionLens>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="align-center inset-x-0 bottom-4 mt-auto flex w-full flex-col items-center sm-landscape:static">
          <LogoWall />
          {/* <motion.div
            className="select-none rounded-full border border-white/10 bg-black/30 px-4 py-1.5 text-[13px] text-white/80 shadow-md backdrop-blur-md animate-delay-[2s] animate-distance-8 md:px-6"
            initial={{ opacity: 0, y: 32, scale: 0.8 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{
              margin: "-20px",
              once: true,
            }}
            transition={{
              duration: 0.2,
              type: "keyframes",
            }}
           >
            <AnimatedShinyText>
              Designed & developed with leading venture funds
            </AnimatedShinyText>
           </motion.div> */}
        </div>
      </div>
    </section>;
}
const Background = t0 => {
  const $ = _c(9);
  const {
    logos
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute inset-x-0 z-[-1] mx-auto hidden h-[calc(var(--size)*3-2px)] w-[calc(var(--size)*9-2px)] self-center bg-body shadow-[inset_0_0_10px_rgba(0,0,0,0.2)] lg:flex" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== logos) {
    t2 = <GridBackground className="squares !top-[72px] isolate transform-gpu !bg-center [--mask-size:82%] [--transparent-position:100%]" logos={logos}>{t1}</GridBackground>;
    $[1] = logos;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="max-w-screen pointer-events-none absolute inset-x-0 top-1/2 hidden h-[350px] -translate-y-[92px] flex-col items-center overflow-hidden lg:flex"><Line className="text-neutral-800/0" delay={5} duration={5} /><Line className="mt-[110px] rotate-180 text-neutral-800/0" delay={5} duration={5} /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute inset-x-0 top-1/2 mt-[72px] hidden -translate-y-1/2 flex-col items-center justify-center lg:flex"><Line className="mr-[551px] -translate-x-1/2 text-neutral-800/0" getPathData={getVerticalPath} direction="vertical" delay={0} duration={10} color="#047857" /></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="absolute inset-x-0 top-1/2 mt-[72px] hidden -translate-y-1/2 -scale-x-100 flex-col items-center justify-center lg:flex"><Line className="mr-[551px] -translate-x-1/2 text-neutral-800/0" getPathData={getVerticalPath} direction="vertical" delay={0} duration={10} color="#047857" /></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="noise pointer-events-none absolute inset-0 z-[-1] h-screen w-full bg-center bg-repeat opacity-50" style={{
      backgroundImage: `url("${noiseImage.src}")`
    }} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t2) {
    t7 = <div aria-hidden="true">{t2}{t3}{t4}{t5}{t6}</div>;
    $[7] = t2;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
};
const getDefaultPathData = () => {
  // Define the path data
  const size = 110;
  const height = size;
  const lineLength = size * 9;
  const additionalLength = (window.innerWidth - lineLength) / 2;
  const pathData = [{
    x: 0,
    y: height
  }, {
    x: additionalLength,
    y: 0
  }, {
    x: 0,
    y: -height
  }, {
    x: lineLength + 1,
    y: 0
  }, {
    x: 0,
    y: height
  }, {
    x: additionalLength,
    y: 0
  }];
  return pathData;
};
const getVerticalPath = () => {
  const size = 110;
  const height = size * 3;
  const width = size * 2;
  //const additionalLength = size * 2;
  const additionalLength = Math.max(size, (window.innerHeight * 0.55 - height) / 2);
  return [{
    x: 0,
    y: 0
  }, {
    x: 0,
    y: -additionalLength
  }, {
    x: -width,
    y: 0
  }, {
    x: 0,
    y: -height
  }, {
    x: width,
    y: 0
  }, {
    x: 0,
    y: -additionalLength
  }];
};
const getCumulativePathStart = pathData => {
  let minX = 0;
  let minY = 0;
  pathData.reduce((acc, {
    x,
    y
  }) => {
    acc.cumulativeX += x;
    acc.cumulativeY += y;
    minX = Math.min(minX, acc.cumulativeX);
    minY = Math.min(minY, acc.cumulativeY);
    return acc;
  }, {
    cumulativeX: 0,
    cumulativeY: 0
  });
  return {
    cumulativeX: minX * -1,
    cumulativeY: minY * -1
  };
};
const Line = t0 => {
  const $ = _c(35);
  const {
    className,
    getPathData: t1,
    color: t2,
    debug: t3,
    direction: t4,
    delay: t5,
    duration: t6,
    initialDelay: t7
  } = t0;
  const getPathData = t1 === undefined ? getDefaultPathData : t1;
  const color = t2 === undefined ? "#4f46e5" : t2;
  const debug = t3 === undefined ? false : t3;
  const direction = t4 === undefined ? "horizontal" : t4;
  const delay = t5 === undefined ? 3 : t5;
  const duration = t6 === undefined ? 5 : t6;
  const initialDelay = t7 === undefined ? 0 : t7;
  const svgRef = useRef(null);
  const id = React.useId();
  const [isGradientHidden] = useState(false);
  let t8;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      width: 0,
      height: 0
    };
    $[0] = t8;
  } else {
    t8 = $[0];
  }
  const [dimensions, setDimensions] = useState(t8);
  const width = dimensions.width;
  const height = dimensions.height;
  let t9;
  if ($[1] !== color || $[2] !== debug || $[3] !== getPathData || $[4] !== id) {
    t9 = () => {
      if (!svgRef.current) {
        return;
      }
      const svg = d3.select(svgRef.current);
      const data = getPathData();
      const {
        cumulativeX: t10,
        cumulativeY: t11
      } = getCumulativePathStart(data);
      let cumulativeX = t10 === undefined ? 0 : t10;
      let cumulativeY = t11 === undefined ? 0 : t11;
      const line = d3.line().x(d => {
        cumulativeX = cumulativeX + d.x;
        cumulativeX;
        return cumulativeX;
      }).y(d_0 => {
        cumulativeY = cumulativeY + d_0.y;
        cumulativeY;
        return cumulativeY;
      });
      const res = svg.append("path").attr("d", line(data)).attr("fill", "none").attr("stroke", `url(#${id})`).attr("stroke-linecap", "round").attr("stroke-width", 1.5).attr("id", `path-${id}`);
      if (debug) {
        res.attr("stroke", color);
      }
      const path = res.node();
      const bbox = path.getBBox();
      svg.attr("viewBox", `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`).attr("width", bbox.width).attr("height", bbox.height);
      setDimensions({
        width: bbox.width,
        height: bbox.height
      });
      if (svgRef.current) {
        svgRef.current.classList.remove("hidden");
      }
    };
    $[1] = color;
    $[2] = debug;
    $[3] = getPathData;
    $[4] = id;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  let t10;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  useEffect(t9, t10);
  if (!dimensions.width || !dimensions.height) {
    return null;
  }
  let t11;
  if ($[7] !== className) {
    t11 = clsx(className, "hidden");
    $[7] = className;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  let t12;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      display: "none"
    };
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  let t13;
  if ($[10] !== id) {
    t13 = e => {
      const el = document.getElementById(id);
      const anims = el?.getAnimations();
      anims?.forEach(_temp);
    };
    $[10] = id;
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  let t14;
  if ($[12] !== direction || $[13] !== height || $[14] !== isGradientHidden || $[15] !== width) {
    t14 = isGradientHidden ? "hidden" : direction === "horizontal" ? {
      x1: [0, width * 2],
      x2: [0, width],
      y1: [0, height],
      y2: [0, height / 2]
    } : {
      x1: [width, width * 2],
      x2: [width * 3, width],
      y1: [-height / 2, height],
      y2: [-height, height]
    };
    $[12] = direction;
    $[13] = height;
    $[14] = isGradientHidden;
    $[15] = width;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== delay || $[18] !== duration || $[19] !== initialDelay) {
    t15 = {
      duration,
      repeat: Infinity,
      repeatDelay: delay,
      ease: "easeInOut",
      delay: initialDelay,
      type: "keyframes"
    };
    $[17] = delay;
    $[18] = duration;
    $[19] = initialDelay;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  let t17;
  if ($[21] !== color) {
    t16 = <stop stopColor={color} stopOpacity="0" />;
    t17 = <stop stopColor={color} stopOpacity="60%" />;
    $[21] = color;
    $[22] = t16;
    $[23] = t17;
  } else {
    t16 = $[22];
    t17 = $[23];
  }
  let t18;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <stop offset="1" stopColor="#10b981000" stopOpacity="0" />;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== id || $[26] !== t13 || $[27] !== t14 || $[28] !== t15 || $[29] !== t16 || $[30] !== t17) {
    t19 = <defs><motion.linearGradient id={id} onAnimationStart={t13} gradientUnits="userSpaceOnUse" animate={t14} transition={t15}>{t16}{t17}{t18}</motion.linearGradient></defs>;
    $[25] = id;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== t11 || $[33] !== t19) {
    t20 = <><svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" className={t11} shapeRendering="pixelated" style={t12}>{t19}</svg></>;
    $[32] = t11;
    $[33] = t19;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  return t20;
};

/* {dimensions.path && (
        <span
          className="flex h-1 w-10 animate-offset rounded-full bg-gradient-to-r from-primary-400/40 to-transparent animate-infinite animate-reverse animate-duration-[5s]"
          ref={animatorRef}
          style={{
            offsetPath: `path('${dimensions.path}')`,
            offsetDistance: "50%",
          }}
        ></span>
      )} */
function _temp(anim) {
  anim.startTime = 0;
}